/**
 * TaskDrawerAssignees Component
 *
 * Renders the assignees and collaborators card in the drawer sidebar:
 * - Assignee organization selector
 * - Assignee user selector
 * - Collaborators list with join/leave functionality
 * - Edit mode for platform users
 *
 * Accessibility:
 * - Form labels properly associated with inputs
 * - ARIA live regions for status updates
 * - Keyboard navigation for collaborator actions
 *
 * @see WCAG 2.1 AA - 3.3.2 Labels or Instructions
 */

import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Divider,
  Form,
  Select,
  Tag,
  Tooltip,
  message,
  Modal,
} from 'antd';
import {
  EditOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import {
  assigneeOptions,
  compareOptionLabels,
  deduplicateUsers,
  matchesOptionLabel,
  getTaskProjectAssigneeGroups,
  getTaskProjects,
} from '../../utils/task.utils';
import { filterEnabledUsers } from '../../../../../core/utils/enabledUserFilter';
import '../../styles/index.scss';

const { Option, OptGroup } = Select;

const TaskDrawerAssignees = ({
  currentTask,
  taskId,
  currentUser,
  form,
  level,
  isEditingAssignee,
  setIsEditingAssignee,
  isCollaborator,
  isCurrentUserAssignee,
  isFollowers,
  updateTaskLoading,
  updateTask,
  fetchTask,
  onTaskUpdate,
  executeWithUnsavedCheck,
  // Data fetching
  fetchBasicOrgs,
  fetchBasicUsers,
  fetchCollaborators,
  basicOrgsDetails,
  basicOrgsDetailsLoading,
  basicUsersDetails,
  basicUsersDetailsLoading,
  collaboratorsData,
  collaboratorsLoading,
  // Team data
  teams,
  teamsLoading,
  fetchTeamsByOrg,
}) => {
  const [showFollowerModal, setShowFollowerModal] = useState(false);
  const [selectedFollowerId, setSelectedFollowerId] = useState(null);

  /**
   * Handle collaborator join/leave toggle
   */
  const handleCollaboratorToggle = () => {
    if (!isCollaborator && (isCurrentUserAssignee || isFollowers)) {
      if (isCurrentUserAssignee) {
        message.warning('Assignees are already collaborators but with more responsibility.');
      } else if (isFollowers) {
        message.warning('Please unfollow first if you want to be a collaborator.');
      }
      return;
    }

    executeWithUnsavedCheck(async () => {
      try {
        await updateTask({
          variables: {
            data: {
              collaborators: !isCollaborator
                ? { connect: { id: currentUser.id } }
                : { disconnect: { id: currentUser.id } },
            },
            where: { id: taskId },
          },
        });
        await fetchTask({
          variables: { where: { id: taskId } },
        });
        message.success(
          !isCollaborator
            ? 'You are now a collaborator of the task.'
            : 'Left the task.'
        );
        if (onTaskUpdate) {
          onTaskUpdate();
        }
      } catch (error) {
        message.error(
          !isCollaborator
            ? 'Failed to be a collaborator.'
            : 'Failed to leave the task.'
        );
      }
    });
  };

  /**
   * Handle user/assignee update submission
   */
  const handleUserUpdate = () => {
    executeWithUnsavedCheck(async () => {
      const values = form.getFieldsValue();
      try {
        const orgUpdate = values.assigneeOrg
          ? { connect: { id: values.assigneeOrg } }
          : currentTask?.assigneeOrg
            ? { disconnect: true }
            : {};

        const newAssignee = values.assigneeUsers;
        let newCollaborators = values.collaborators || [];

        const currentAssignees = currentTask?.assigneeUsers || [];
        const currentAssigneeIds = currentAssignees.map((user) => user.id);
        const previousCollaborators = currentTask?.collaborators || [];

        const assigneeUsers = {};
        const collaborators = {};

        // Auto-promote: if new assignee is a collaborator, auto-remove from collaborators
        if (newAssignee && newCollaborators.includes(newAssignee)) {
          newCollaborators = newCollaborators.filter(id => id !== newAssignee);
          form.setFieldsValue({ collaborators: newCollaborators });
          message.info('Collaborator promoted to assignee.');
        }

        // Detect outgoing assignee for collaborator promotion
        const outgoingAssigneeId = currentAssigneeIds.length > 0 && newAssignee && newAssignee !== currentAssigneeIds[0]
          ? currentAssigneeIds[0]
          : null;
        const outgoingAssignee = outgoingAssigneeId
          ? currentAssignees.find(u => u.id === outgoingAssigneeId)
          : null;

        if (outgoingAssignee) {
          const addAsCollaborator = await new Promise((resolve) => {
            Modal.confirm({
              title: 'Add previous assignee as collaborator?',
              content: `Add ${startCase(outgoingAssignee.name || outgoingAssignee.username)} as a collaborator so they continue receiving updates on this ticket?`,
              okText: 'Yes, add as collaborator',
              cancelText: 'No, skip',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (addAsCollaborator && !newCollaborators.includes(outgoingAssigneeId)) {
            newCollaborators = [...newCollaborators, outgoingAssigneeId];
          }
        }

        // Handle assignee disconnection
        if (currentAssignees.length > 0) {
          const disconnectIds = currentAssigneeIds.filter((id) => id !== newAssignee);
          if (disconnectIds.length > 0) {
            assigneeUsers.disconnect = disconnectIds.map((id) => ({ id }));
          }
        }

        // Handle assignee connection
        if (newAssignee && !currentAssigneeIds.includes(newAssignee)) {
          assigneeUsers.connect = { id: newAssignee };
        }

        // Handle collaborators
        const usersToConnect = newCollaborators.filter(
          (id) => !previousCollaborators.some((user) => user.id === id)
        );
        const usersToDisconnect = previousCollaborators.filter(
          (user) => !newCollaborators.includes(user.id)
        );

        if (usersToDisconnect.length > 0) {
          collaborators.disconnect = usersToDisconnect.map((user) => ({ id: user.id }));
        }
        if (usersToConnect.length > 0) {
          collaborators.connect = usersToConnect.map((id) => ({ id }));
        }

        // Handle team update — only when the team actually changed. Disconnecting and reconnecting
        // the same team in one mutation can net out to "removed" in Prisma, which wiped the team on
        // an unrelated edit (e.g. just changing the assignee).
        const teamsUpdate = {};
        const currentTeamIds = (currentTask?.teams || []).map((t) => t.id);
        const newTeamId = values.teamId || null;
        const previousTeamId = currentTeamIds[0] || null;

        if (newTeamId !== previousTeamId) {
          if (currentTeamIds.length > 0) {
            teamsUpdate.disconnect = currentTeamIds.map((id) => ({ id }));
          }
          if (newTeamId) {
            teamsUpdate.connect = [{ id: newTeamId }];
          }
        }

        // "Add new, keep old": when the team is changed, add the new team's subscribers
        // (individuals + members of teams that subscribe to it) as collaborators. Existing
        // collaborators are kept; nobody is auto-removed here.
        if (newTeamId && newTeamId !== previousTeamId) {
          const selectedTeam = (teams || []).find((t) => t.id === newTeamId);
          if (selectedTeam) {
            const targetTeamMemberIds = new Set((selectedTeam.members || []).map((m) => m.id));
            const subscriberIds = [
              ...(selectedTeam.subscribers || []).map((s) => s.id),
              ...(selectedTeam.subscriberTeams || []).flatMap((st) =>
                (st.members || []).map((m) => m.id)
              ),
            ];
            const alreadyConnectedIds = new Set([
              ...previousCollaborators.map((u) => u.id),
              ...((collaborators.connect || []).map((c) => c.id)),
            ]);
            const autoCollaboratorIds = [...new Set(subscriberIds)].filter(
              (id) => id !== newAssignee && !targetTeamMemberIds.has(id) && !alreadyConnectedIds.has(id)
            );
            if (autoCollaboratorIds.length > 0) {
              collaborators.connect = [
                ...(collaborators.connect || []),
                ...autoCollaboratorIds.map((id) => ({ id })),
              ];
            }
          }
        }

        await updateTask({
          variables: {
            data: {
              assigneeOrg: orgUpdate,
              assigneeUsers,
              collaborators,
              ...(Object.keys(teamsUpdate).length > 0 ? { teams: teamsUpdate } : {}),
              activityLog: 'Updated Assignee',
            },
            where: { id: taskId },
          },
        });

        await fetchTask({
          variables: { where: { id: taskId } },
        });
        message.success('Task assignee updated successfully.');
        setIsEditingAssignee(false);
        if (onTaskUpdate) {
          onTaskUpdate();
        }
      } catch (error) {
        message.error('Failed to update task assignee.');
        console.error('Update failed:', error);
      }
    });
  };

  /**
   * Handle cancel edit
   */
  const handleCancelEdit = useCallback(() => {
    setIsEditingAssignee(false);
    form.setFieldsValue({
      assigneeOrg: currentTask?.assigneeOrg?.id || null,
      assigneeUsers:
        currentTask?.assigneeUsers?.[0]?.id || undefined,
      collaborators:
        currentTask?.collaborators?.map((user) => user.id) || [],
      teamId: currentTask?.teams?.[0]?.id || null,
    });
  }, [currentTask, form, setIsEditingAssignee]);

  /**
   * Handle follower promotion modal
   */
  const handleFollowerPromotion = async () => {
    try {
      message.loading('Removing follower...', 0);
      await updateTask({
        variables: {
          where: { id: taskId },
          data: {
            followers: {
              disconnect: [{ id: selectedFollowerId }],
            },
          },
        },
      });
      await fetchTask({
        variables: { where: { id: taskId } },
      });
      message.destroy();
      message.success('Follower removed successfully');
      form.setFieldValue('assigneeUsers', selectedFollowerId);
      setShowFollowerModal(false);
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error) {
      message.destroy();
      message.error(`Failed to remove follower: ${error.message}`);
    }
  };

  return (
    <>
      <Card
        size="small"
        className="task-drawer-full__card"
        role="region"
        aria-labelledby="assignees-heading"
      >
        {/* Assignees Section Header */}
        <div className="task-drawer-full__card-header">
          <span id="assignees-heading" className="task-drawer-full__card-title">Assigned To</span>
          {!isEditingAssignee && level === 'PLATFORM' && (
            <EditOutlined
              className="task-drawer-full__edit-icon"
              onClick={() => {
                setIsEditingAssignee(true);
                fetchBasicOrgs();
                // Fetch teams even for team-only tasks (no assigneeOrg) — fetchTeamsByOrg
                // falls back to the current user's org.
                fetchTeamsByOrg(currentTask?.assigneeOrg?.id);
              }}
              role="button"
              aria-label="Edit assignment"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsEditingAssignee(true);
                  fetchBasicOrgs();
                  fetchTeamsByOrg(currentTask?.assigneeOrg?.id);
                }
              }}
            />
          )}
        </div>

        <Form form={form} layout="vertical">
          {/* Team — hidden for project-linked tasks (assigned via the project, not a team) */}
          {!currentTask?.project && (
          <Form.Item
            label={
              <span>
                Team
                {isEditingAssignee && (
                  <Tooltip title="Changing the team adds the new team's subscribers as collaborators and keeps everyone already on this task — existing collaborators are not removed.">
                    <InfoCircleOutlined style={{ marginLeft: 6, color: '#faad14', cursor: 'help' }} />
                  </Tooltip>
                )}
              </span>
            }
            name="teamId"
            className="task-drawer-full__form-item"
          >
            {isEditingAssignee ? (
              <Select
                className="custom-select"
                placeholder="Select Team (optional)"
                showSearch
                allowClear
                loading={teamsLoading}
                onFocus={() => fetchTeamsByOrg(form.getFieldValue('assigneeOrg'))}
                onSearch={(searchText) =>
                  fetchTeamsByOrg(form.getFieldValue('assigneeOrg'), searchText)
                }
                onChange={(value) => {
                  form.setFieldValue('assigneeUsers', undefined);
                }}
                filterOption={false}
                aria-label="Select team"
              >
                {(teams || []).map((team) => (
                  <Option key={team.id} value={team.id}>
                    {team.name}
                  </Option>
                ))}
              </Select>
            ) : (
              <div>
                {currentTask?.teams?.length > 0 ? (
                  currentTask.teams.map((team) => (
                    <Tag key={team.id} color="blue">
                      {team.name}
                    </Tag>
                  ))
                ) : (
                  <Text className="task-drawer-full__assignee-text">-</Text>
                )}
              </div>
            )}
          </Form.Item>
          )}

          {/* Assignee User — re-renders when assigneeOrg or teamId changes */}
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.assigneeOrg !== cur.assigneeOrg || prev.teamId !== cur.teamId}
          >
            {() => {
              const selectedTeamId = form.getFieldValue('teamId');
              const isProject = !!currentTask?.project;

              // When a team is selected its members are the source of truth (works even for
              // team-only tasks where basicUsersDetails was never fetched); otherwise use the
              // org's user list. Team members aren't server-filtered, so drop disabled ones here.
              const filteredUsers = selectedTeamId
                ? (() => {
                    const selectedTeam = (teams || []).find((t) => t.id === selectedTeamId);
                    return selectedTeam && selectedTeam.members
                      ? filterEnabledUsers(selectedTeam.members)
                      : [];
                  })()
                : basicUsersDetails;

              return (
                <Form.Item
                  label={
                    <span>
                      Assignee
                      {isProject && (
                        <Text className="task-drawer-full__assignee-hint">
                          (from project team)
                        </Text>
                      )}
                    </span>
                  }
                  name="assigneeUsers"
                  className="task-drawer-full__form-item"
                  extra={
                    isEditingAssignee ? (
                      <Text className="task-drawer-full__helper-text">
                        Optional — pick any user, or select a team to narrow to its members
                      </Text>
                    ) : null
                  }
                >
                  {isEditingAssignee ? (
                    <Select
                      className="custom-select"
                      placeholder={isProject ? 'Select from project team' : 'Select Assignee'}
                      showSearch
                      allowClear
                      onSearch={(e) => !isProject && fetchBasicUsers(e)}
                      onFocus={() => !isProject && fetchBasicUsers()}
                      loading={basicUsersDetailsLoading}
                      filterOption={matchesOptionLabel}
                      filterSort={compareOptionLabels}
                      onChange={(selectedId) => {
                        const followers = currentTask?.followers || [];
                        const followerIds = followers.map((user) => user.id);
                        if (followerIds.includes(selectedId)) {
                          setShowFollowerModal(true);
                          setSelectedFollowerId(selectedId);
                        }
                      }}
                      aria-label="Select assignee"
                    >
                      {isProject ? (
                        getTaskProjectAssigneeGroups(
                          getTaskProjects(currentTask),
                          currentTask.assigneeUsers
                        ).map((group) => (
                          <OptGroup key={group.id} label={group.name}>
                            {group.members.map((user) => (
                              <Option key={`${group.id}-${user.id}`} value={user.id}>
                                {startCase(user.name || user.username)}
                              </Option>
                            ))}
                          </OptGroup>
                        ))
                      ) : (
                        deduplicateUsers(
                          filteredUsers.concat(
                            currentTask?.assigneeUsers?.filter(
                              (u) => !filteredUsers.some((bu) => bu.id === u.id)
                            ) || []
                          )
                        ).map((user) => (
                          <Option key={user.id} value={user.id}>
                            {startCase(user.name || user.username)}
                          </Option>
                        ))
                      )}
                    </Select>
                  ) : (
                    <div>
                      {currentTask?.assigneeUsers?.length > 0 ? (
                        currentTask.assigneeUsers.map((user) => (
                          <div key={user.id} className="task-drawer-full__assignee-item">
                            <Text className="task-drawer-full__assignee-text">
                              {startCase(user.name || user.username)}
                            </Text>
                          </div>
                        ))
                      ) : (
                        <Text className="task-drawer-full__assignee-text">-</Text>
                      )}
                    </div>
                  )}
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>

        <Divider className="task-drawer-full__divider" />

        {/* Collaborators Section */}
        <div className="task-drawer-full__card-header">
          <div className="task-drawer-full__card-title">
            <UserOutlined
              className="task-drawer-full__card-icon--success"
              aria-hidden="true"
            />
            <span>Collaborators</span>
          </div>
          {level === 'PLATFORM' && !isEditingAssignee && (
            <Button
              type="text"
              size="small"
              onClick={handleCollaboratorToggle}
              disabled={updateTaskLoading}
              icon={isCollaborator ? <MinusOutlined /> : <PlusOutlined />}
              className={
                isCollaborator
                  ? 'task-drawer-full__card-action--leave'
                  : 'task-drawer-full__card-action--join'
              }
              aria-label={isCollaborator ? 'Leave as collaborator' : 'Join as collaborator'}
              aria-pressed={isCollaborator}
            >
              {isCollaborator ? 'Leave' : 'Join'}
            </Button>
          )}
        </div>

        {!isEditingAssignee ? (
          <>
            {currentTask?.collaborators?.length > 0 ? (
              <div className="task-drawer-full__avatar-stack" role="list" aria-label="Task collaborators">
                {currentTask.collaborators.map((collaborator) => (
                  <Tooltip
                    key={collaborator.id}
                    title={startCase(collaborator.name || collaborator.username)}
                  >
                    <div role="listitem">
                      <AvatarS3
                        imgKey={collaborator.avatar?.key}
                        username={collaborator.name || collaborator.username}
                        avatarProps={{
                          size: 28,
                          'aria-label': startCase(collaborator.name || collaborator.username),
                        }}
                      />
                    </div>
                  </Tooltip>
                ))}
              </div>
            ) : (
              <Text type="secondary" className="task-drawer-full__empty-text">
                No collaborators yet
              </Text>
            )}
          </>
        ) : (
          <>
            <Form form={form} layout="vertical">
              <Form.Item name="collaborators" className="task-drawer-full__form-item">
                <Select
                  className="custom-select"
                  mode="multiple"
                  placeholder="Select collaborator(s)"
                  onSearch={fetchCollaborators}
                  onFocus={() => fetchCollaborators()}
                  loading={collaboratorsLoading}
                  filterOption={false}
                  filterSort={(optionA, optionB) =>
                    optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                  }
                  aria-label="Select collaborators"
                >
                  {assigneeOptions(collaboratorsData, currentTask?.collaborators).map((user) => (
                    <Option key={user.id} value={user.id}>
                      {startCase(user.name || user.username)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
            <Divider className="task-drawer-full__divider" />
            <div className="task-drawer-full__edit-actions">
              <Button
                type="primary"
                onClick={handleUserUpdate}
                disabled={updateTaskLoading}
                size="small"
                className="task-drawer-full__save-btn"
                aria-label="Save assignment changes"
              >
                {updateTaskLoading && <LoadingOutlined aria-hidden="true" />} Save
              </Button>
              <Button
                onClick={handleCancelEdit}
                size="small"
                className="task-drawer-full__cancel-btn"
                disabled={updateTaskLoading}
                aria-label="Cancel editing"
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* Follower Warning Modal */}
      <Modal
        centered
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              form.setFieldValue('assigneeUsers', undefined);
              setShowFollowerModal(false);
            }}
            disabled={updateTaskLoading}
          >
            Cancel
          </Button>,
          <Button
            key="ok"
            type="primary"
            disabled={updateTaskLoading}
            onClick={handleFollowerPromotion}
          >
            {updateTaskLoading && <LoadingOutlined aria-hidden="true" />} Remove
          </Button>,
        ]}
        title="Remove Follower"
        open={showFollowerModal}
        onCancel={() => {
          form.setFieldValue('assigneeUsers', undefined);
          setShowFollowerModal(false);
        }}
        aria-labelledby="follower-modal-title"
      >
        <p id="follower-modal-title">
          This user is a follower. Do you want to promote them as an assignee and remove them from
          followers?
        </p>
      </Modal>
    </>
  );
};

TaskDrawerAssignees.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    assigneeOrg: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      level: PropTypes.string,
    }),
    assigneeUsers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        username: PropTypes.string,
      })
    ),
    collaborators: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        username: PropTypes.string,
        avatar: PropTypes.shape({
          key: PropTypes.string,
        }),
      })
    ),
    followers: PropTypes.array,
    project: PropTypes.shape({
      id: PropTypes.string,
      owner: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        username: PropTypes.string,
      }),
      team: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          username: PropTypes.string,
        })
      ),
    }),
  }),
  taskId: PropTypes.string.isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  form: PropTypes.object.isRequired,
  level: PropTypes.string,
  isEditingAssignee: PropTypes.bool,
  setIsEditingAssignee: PropTypes.func.isRequired,
  isCollaborator: PropTypes.bool,
  isCurrentUserAssignee: PropTypes.bool,
  isFollowers: PropTypes.bool,
  updateTaskLoading: PropTypes.bool,
  updateTask: PropTypes.func.isRequired,
  fetchTask: PropTypes.func.isRequired,
  onTaskUpdate: PropTypes.func,
  executeWithUnsavedCheck: PropTypes.func.isRequired,
  fetchBasicOrgs: PropTypes.func.isRequired,
  fetchBasicUsers: PropTypes.func.isRequired,
  fetchCollaborators: PropTypes.func.isRequired,
  basicOrgsDetails: PropTypes.array,
  basicOrgsDetailsLoading: PropTypes.bool,
  basicUsersDetails: PropTypes.array,
  basicUsersDetailsLoading: PropTypes.bool,
  collaboratorsData: PropTypes.array,
  collaboratorsLoading: PropTypes.bool,
  teams: PropTypes.array,
  teamsLoading: PropTypes.bool,
  fetchTeamsByOrg: PropTypes.func,
};

TaskDrawerAssignees.defaultProps = {
  currentTask: null,
  level: null,
  isEditingAssignee: false,
  isCollaborator: false,
  isCurrentUserAssignee: false,
  isFollowers: false,
  updateTaskLoading: false,
  onTaskUpdate: null,
  basicOrgsDetails: [],
  basicOrgsDetailsLoading: false,
  basicUsersDetails: [],
  basicUsersDetailsLoading: false,
  collaboratorsData: [],
  collaboratorsLoading: false,
  teams: [],
  teamsLoading: false,
  fetchTeamsByOrg: () => {},
};

export default TaskDrawerAssignees;
